import React, { createContext, useContext, useEffect, useState } from "react";

const STRINGS = {
  en: {
    appName: "EGNS — Fundamental Nursing E-learning",
    tagline: "A calm space to master the fundamentals of nursing.",
    signIn: "Sign in with Google",
    loading: "Loading…",
    translating: "Translating to Arabic…",
    translateToArabic: "العربية",
    switchToEnglish: "English",
    lesson: "Lesson",
    flashcards: "Flashcards",
    quiz: "Quiz",
    scenario: "Scenario",
    summary: "Summary",
    source: "Textbook",
    chapters: "Chapters",
    yourCourse: "Your course",
    chapter: "Chapter",
    start: "Start",
    continue: "Continue",
    open: "Open",
    logout: "Sign out",
    welcomeBack: "Welcome back",
    progress: "Progress",
    flip: "Tap to flip",
    next: "Next",
    previous: "Previous",
    submit: "Submit answer",
    correct: "Correct",
    incorrect: "Incorrect",
    tryAgain: "Try again",
    seeResults: "See results",
    reset: "Reset",
    clinicalPearls: "Clinical pearls",
    keyTakeaways: "Key takeaways",
    mustRemember: "Must remember",
    examTips: "Exam tips",
    teachingPoints: "Teaching points",
    setting: "Setting",
    patient: "Patient",
    situation: "Situation",
    discussionQuestions: "Discussion questions",
    idealAnswer: "Model answer",
    score: "Score",
    of: "of",
    backToChapters: "Back to chapters",
    generatingContent: "Preparing your content…",
    retry: "Retry",
    failedLoad: "Could not load content.",
    keyTopics: "Key topics",
    viewChapter: "View chapter",
    studentPortal: "Student Portal",
    heroTagline: "EGNS — The right start begins here",
    heroTitle: "Professional Nursing Education in English or Arabic",
    heroSub: "Learn Fundamentals of Nursing through AI-supported lessons, flashcards, quizzes, and realistic clinical scenarios designed for modern students.",
    featureLessons: "Structured lessons",
    featureLessonsSub: "Clear, topic-by-topic chapters written for first-year nursing students.",
    featureFlash: "Active recall",
    featureFlashSub: "Flip-cards that build durable memory around core concepts.",
    featureQuiz: "Clinical thinking",
    featureQuizSub: "MCQs and bedside scenarios that mirror real exams.",
    featureLang: "Bilingual",
    featureLangSub: "One-click switch to Modern Standard Arabic with exact meaning preserved.",
  },
  ar: {
    appName: "EGNS — أساسيات التمريض التعليم الإلكتروني",
    tagline: "مساحة هادئة لإتقان أساسيات التمريض.",
    signIn: "تسجيل الدخول بجوجل",
    loading: "جارٍ التحميل…",
    translating: "جارٍ الترجمة إلى العربية…",
    translateToArabic: "العربية",
    switchToEnglish: "English",
    lesson: "الدرس",
    flashcards: "بطاقات",
    quiz: "اختبار",
    scenario: "سيناريو",
    summary: "ملخّص",
    source: "المرجع",
    chapters: "الفصول",
    yourCourse: "مقرّرك",
    chapter: "الفصل",
    start: "ابدأ",
    continue: "متابعة",
    open: "افتح",
    logout: "تسجيل الخروج",
    welcomeBack: "مرحبًا بعودتك",
    progress: "التقدّم",
    flip: "اضغط للقلب",
    next: "التالي",
    previous: "السابق",
    submit: "إرسال الإجابة",
    correct: "إجابة صحيحة",
    incorrect: "إجابة خاطئة",
    tryAgain: "حاول مجددًا",
    seeResults: "عرض النتائج",
    reset: "إعادة",
    clinicalPearls: "لآلئ سريرية",
    keyTakeaways: "الخلاصات",
    mustRemember: "يجب تذكّره",
    examTips: "نصائح للامتحان",
    teachingPoints: "نقاط تعليمية",
    setting: "المكان",
    patient: "المريض",
    situation: "الحالة",
    discussionQuestions: "أسئلة للنقاش",
    idealAnswer: "إجابة نموذجية",
    score: "النتيجة",
    of: "من",
    backToChapters: "عودة إلى الفصول",
    generatingContent: "نقوم بتحضير المحتوى…",
    retry: "إعادة المحاولة",
    failedLoad: "تعذّر تحميل المحتوى.",
    keyTopics: "المواضيع الرئيسية",
    viewChapter: "عرض الفصل",
    studentPortal: "بوابة الطالب",
    heroTitle: "تعليم تمريضيّ احترافيّ بالإنجليزية أو العربية",
    heroSub: "تعلّم أساسيات التمريض من خلال دروس وبطاقات واختبارات وسيناريوهات سريرية واقعية مدعومة بالذكاء الاصطناعي، مصمّمة لطلاب اليوم.",
    featureLessons: "دروس منظّمة",
    featureLessonsSub: "فصول واضحة موضوعًا بعد موضوع لطلاب السنة الأولى.",
    featureFlash: "استرجاع نشط",
    featureFlashSub: "بطاقات قابلة للقلب تبني ذاكرة متينة للمفاهيم الأساسية.",
    featureQuiz: "تفكير سريري",
    featureQuizSub: "أسئلة اختيار من متعدد وسيناريوهات تحاكي الامتحانات الحقيقية.",
    featureLang: "ثنائية اللغة",
    featureLangSub: "التبديل بنقرة واحدة إلى الفصحى مع الحفاظ على المعنى بدقة.",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("lang", lang);
  }, [lang]);

  const toggle = React.useCallback(
    () => setLang((l) => (l === "en" ? "ar" : "en")),
    [],
  );
  const t = React.useCallback(
    (key) => STRINGS[lang][key] ?? STRINGS.en[key] ?? key,
    [lang],
  );

  const value = React.useMemo(
    () => ({ lang, setLang, toggle, t, isRTL: lang === "ar" }),
    [lang, toggle, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be inside LanguageProvider");
  return ctx;
}
